import React, { useReducer } from 'react';

import type { AccountOption } from 'src/components';
import { AlineDialog, ShareDocumentDialogBody } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { doNothing } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

export interface ShareDocumentDialogState {
  alert: {
    msg: string;
    showAlert: boolean;
  } | null;
  autocompleteValue: AccountOption[];
  message: string;
  notifyRecipients: boolean;
  permissionRole: Apollo.PermissionRole;
}

export enum ShareDocumentDialogActionType {
  SET_DIALOG_STATE = 'SET_DIALOG_STATE',
}

export interface ShareDocumentDialogAction {
  payload: Partial<ShareDocumentDialogState>;
  type: ShareDocumentDialogActionType.SET_DIALOG_STATE;
}

const initialShareDocumentDialogState: ShareDocumentDialogState = {
  alert: null,
  autocompleteValue: [],
  message: '',
  notifyRecipients: true,
  permissionRole: Apollo.PermissionRole.Viewer,
};

function shareDocumentDialogReducer(
  state: ShareDocumentDialogState,
  action: ShareDocumentDialogAction
): ShareDocumentDialogState {
  switch (action.type) {
    case ShareDocumentDialogActionType.SET_DIALOG_STATE:
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

const useStyles = makeStyles()(theme => ({
  container: {
    width: theme.dialogStyles.mediumDialogWidth,
  },
}));

interface ShareDocumentDialogProps {
  documentId: UUID;
  handleClose: () => void;
  open: boolean;
  initialState?: Partial<ShareDocumentDialogState>;
  onExit?: () => void;
}

export function ShareDocumentDialog({
  documentId,
  handleClose,
  initialState,
  onExit = doNothing,
  open,
}: ShareDocumentDialogProps): JSX.Element | null {
  const { classes } = useStyles();
  const [dialogState, dialogDispatch] = useReducer(
    shareDocumentDialogReducer,
    initialShareDocumentDialogState
  );

  return (
    <AlineDialog
      TransitionProps={{
        onEnter: () => {
          if (initialState)
            return dialogDispatch({
              payload: initialState,
              type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
            });
        },
        onExit: () => {
          onExit();
          // Allow the dialog to close before resetting state to prevent a UI flicker when the Dialog re-renders before closing down.
          setTimeout(() => {
            dialogDispatch({
              payload: initialShareDocumentDialogState,
              type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
            });
          }, 250);
        },
      }}
      classes={{ paperWidthMd: classes.container }}
      open={open}
      title="Share"
      onClose={handleClose}
    >
      <ShareDocumentDialogBody
        closeDialog={handleClose}
        dialogDispatch={dialogDispatch}
        dialogState={dialogState}
        documentId={documentId}
      />
    </AlineDialog>
  );
}
